'use client';

import { Button, Label, Link, TextInput } from '@magicschool/spellbook';
import type { Language } from '@magicschool/supabase/types';
import { getStudentUrl } from '@magicschool/utils/nextjs/url';
import { useStore } from 'features/store';
import NextLink from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AuthContainer } from '@/components/_containers/AuthContainer';
import { AuthCaptchaWidget, useAuthCaptcha } from '@/features/auth/AuthCaptcha';
import { EXISTING_STUDENT_ACCOUNT_ERROR_CODE } from '@/features/auth/existingStudentAccount';
import { getCleverErrorMessageId } from '@/features/externalSso/cleverErrorMessages';
import { useNextUrl } from '@/hooks/useNextUrl';
import { analyticsTrack } from '@/util/amplitude/analyticsClient';
import { formatQueryParams } from '@/util/queryParams';
import { AlertComponent } from '../AlertComponentClient';
import { AuthHeaderClient } from '../AuthHeaderClient';
import { LegalInfoClient } from '../LegalInfoClient';
import { RoleStep } from '../RoleStep';
import { SSOClient } from '../SSOClient';

const IS_EXPIRED_URL_SEGMENT = 'error_description=email+link+is+invalid+or+has+expired';

export const SignInClient = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = useNextUrl();
  const deleted = searchParams.get('deleted') === 'true';
  const studentDupeAccount = searchParams.get('error') === EXISTING_STUDENT_ACCOUNT_ERROR_CODE;
  const cleverErrorMessageId = getCleverErrorMessageId(searchParams.get('error'));
  const intl = useIntl();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const { loading, setField, signIn, roleSelected, formAlert } = useStore(
    ({ AuthFormStoreData: af }) => ({
      loading: af.loading,
      setField: af.setField,
      signIn: af.signIn,
      roleSelected: af.roleSelected,
      formAlert: af.formAlert,
    }),
    [],
  );

  const captcha = useAuthCaptcha();

  const nextRedirectUrl = `${next}${formatQueryParams(searchParams, ['next'])}`;

  const isExpired = window.location.href.toLowerCase().includes(IS_EXPIRED_URL_SEGMENT) || formAlert?.message === 'Auth session missing!';
  useEffect(() => {
    if (isExpired) {
      router.push('/auth/expired-token');
    }
  }, [isExpired, router]);

  useEffect(() => {
    if (deleted) setField('formAlert')({ type: 'success', messageId: 'profile.delete.success', page: 'User:Deleted' });
  }, [setField, deleted]);

  useEffect(() => {
    if (studentDupeAccount) {
      setField('formAlert')({ type: 'error', messageId: 'auth.existing-student-account', page: 'SignUp:SSOStudentError', duration: 5000 });
      setTimeout(() => {
        router.push(getStudentUrl());
      }, 5000);
    }
  }, [setField, router, studentDupeAccount]);

  useEffect(() => {
    if (!cleverErrorMessageId) return;
    setField('formAlert')({
      type: 'error',
      messageId: cleverErrorMessageId,
      page: 'SignIn:Error',
      duration: 8000,
    });
  }, [setField, cleverErrorMessageId]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    analyticsTrack('SignIn:Clicked');
    signIn(email, password, nextRedirectUrl, router, intl.locale as Language, captcha.token).finally(captcha.reset);
  };

  return (
    <AuthContainer>
      <AuthContainer.Content size={roleSelected ? 'sm' : 'full'}>
        <AlertComponent />
        {!roleSelected ? (
          <RoleStep />
        ) : (
          <>
            <AuthHeaderClient id="auth.educator-sign-in" />
            <form className="w-full flex flex-col gap-2" onSubmit={onSubmit}>
              <Label className="sr-only" htmlFor="email-input">
                {intl.formatMessage({ id: 'profile.fields.email' })}
              </Label>
              <TextInput
                autoComplete="email"
                id="email-input"
                size="lg"
                onChange={(e) => setEmail(e.target.value)}
                placeholder={intl.formatMessage({ id: 'profile.fields.email' })}
                type="email"
                value={email}
              />
              <Label className="sr-only" htmlFor="password-input">
                {intl.formatMessage({ id: 'auth.password' })}
              </Label>
              <TextInput
                autoComplete="current-password"
                id="password-input"
                size="lg"
                onChange={(e) => setPassword(e.target.value)}
                placeholder={intl.formatMessage({ id: 'auth.password' })}
                type={showPassword ? 'text' : 'password'}
                value={password}
              >
                <TextInput.TrailingAdornment overrideDefault>
                  <Button
                    onClick={() => {
                      setShowPassword(!showPassword);
                      analyticsTrack('SignIn:TogglePasswordVisibilityClicked', { visible: showPassword });
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    <FormattedMessage id={showPassword ? 'input_panel.hide' : 'input_panel.show'} />
                  </Button>
                </TextInput.TrailingAdornment>
              </TextInput>
              <AuthCaptchaWidget captcha={captcha} action="signin" />
              <Button fullWidth type="submit" isLoading={loading} disabled={loading || captcha.pending} size="lg">
                <FormattedMessage id="auth.sign-in" />
              </Button>
              <SSOClient buttonAction="auth.sign-in" next={nextRedirectUrl} />
            </form>
            <div className="flex flex-col items-center gap-2">
              <LegalInfoClient location="SignIn" />
              <div className="flex justify-center gap-4 mt-4">
                <Link asChild onClick={() => analyticsTrack('SignIn:CreateAccountClicked')}>
                  <NextLink href={`/auth/signup${next ? `?next=${next}` : ''}`}>
                    <FormattedMessage id="auth.create-account" />
                  </NextLink>
                </Link>
                <Link asChild onClick={() => analyticsTrack('SignIn:ForgotPasswordClicked')}>
                  <NextLink href={`/auth/forgotpass${next ? `?next=${next}` : ''}`}>
                    <FormattedMessage id="auth.forgot-password" />
                  </NextLink>
                </Link>
              </div>
              <Link href={getStudentUrl()} onClick={() => analyticsTrack('SignIn:NotEducatorClicked')}>
                <FormattedMessage id="auth.not-educator" />
              </Link>
            </div>
          </>
        )}
      </AuthContainer.Content>
      <AuthContainer.Footer analyticsEventName />
    </AuthContainer>
  );
};
